import { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
//toast
import { toast } from "react-toastify";

//mui
import { TextField, InputAdornment, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

//formik
import * as Yup from "yup";
import { useFormik } from "formik";

//services
import { registerClient } from "../graphql/services";

//Translation
import useTranslation from "../hooks/useTranslation";
import Spinner from "../components/Spiner";

const SignupForm = () => {
  //hooks
  const router = useRouter();
  const { t } = useTranslation();

  //password eye
  const [showPassword, setShowPassword] = useState(false);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  //form
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const registerAsClientSchema = Yup.object().shape({
    firstName: Yup.string().required("Le prénom est requis"),
    // username: Yup.string().required(t("Nom d'utilisateur est nécessaire")),
    username: Yup.string()
      .matches(
        /^(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._]+(?<![_.])$/,

        "Nom d'utilisateur invalide. lettres et chiffres uniquement, sans commencer ni se terminer par '.' ou '_', et non __ ou _. ou ._ ou .. à l'intérieur."
      )
      .min(8, "Le nom d'utilisateur doit contenir au moins 8 caractères")
      .max(20, "Le nom d'utilisateur doit contenir au maximum 20 caractères")
      .required("Nom d'utilisateur est nécessaire"),
    lastName: Yup.string().required("Le nom est obligatoire"),
    phone: Yup.string()
      .matches(/^(5|6|7)[0-9]{8}$/, "Le numéro de téléphone n'est pas valide")
      .required("ce champ est nécessaire à remplir"),
    email: Yup.string()
      .matches(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        "Invalid email"
      )
      .required("Email est nécessaire à remplir"),
    psw: Yup.string()
      .min(8, "Le mot de passe doit être au moins de 8 caractères")
      .matches(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre")
      .matches(
        /[A-Z]/,
        "Le mot de passe doit contenir au moins une lettre majuscule"
      )
      .required("le mot de passe est nécessaire à remplir"),
    address: Yup.string().required("ce champ est nécessaire à remplir"),
    province: Yup.string().required("ce champ est nécessaire à remplir"),
    wilaya: Yup.string().required("ce champ est nécessaire à remplir"),
    postalCode: Yup.number("doit être un nombre").required(
      "ce champ est nécessaire à remplir"
    ),
  });

  const initialValues = useMemo(
    () => ({
      firstName: "",
      lastName: "",
      email: "",
      psw: "",
      phone: "",
      username: "",
      address: "",
      province: "",
      wilaya: "",
      postalCode: "",
    }),
    []
  );

  const formik = useFormik({
    initialValues,
    validationSchema: registerAsClientSchema,
    onSubmit: async (values) => {
      registerAsClient(values);
    },
  });

  const registerAsClient = async (variables) => {
    setLoading(true);
    const {
      firstName,
      lastName,
      email,
      psw: password,
      phone,
      username,
    } = variables;
    const { data, error, message } = await registerClient({
      where: {
        username,
        firstName,
        lastName,
        password,
        email,
        dateOfBirth: "2001-07-30",
        address: {
          building: variables.address,
          city: variables.province,
          state: variables.wilaya,
          street: "",
          zipCode: variables.postalCode,
        },
        phoneNumber: `+213${phone.slice(0, 10)}`,
        profilePictureUrl: null,
      },
    });
    setLoading(false);
    if (error) {
      if (message === "User with this email already exists") {
        toast.info(t("Il existe déjà un utilisateur avec cet adresse email"), {
          autoClose: 3000,
          hideProgressBar: true,
        });
      } else if (message === "User with this phone number already exists") {
        toast.info(t("le numero de telephone déja utilisé"), {
          autoClose: 3000,
          hideProgressBar: true,
        });
      } else if (message === "User with this username already exists") {
        toast.info(t("le nom d'utilisateur est déja utilisé"), {
          autoClose: 3000,
          hideProgressBar: true,
        });
      }
    } else if (data) {
      toast.info(
        t(
          "Votre compte a été créé avec succès ! Veuillez vérifier vos e-mails pour activer votre compte."
        ),
        {
          autoClose: 3000,
          hideProgressBar: true,
        }
      );
      router.push("/login");
    }
  };

  return (
    <div className="max-w-[1366px] min-h-screen mx-auto pt-[109px]">
      <div className="flex justify-center items-center h-full lg:py-[60px] px-[30px] py-[30px]">
        <div className="flex flex-col items-center gap-[30px] bg-white">
          <h2 className="font-bold text-[40px] leading-5 font-myriad-bold">
            {t("S'inscrire")}
          </h2>
          <form
            onSubmit={formik.handleSubmit}
            className="flex lg:flex-row flex-col lg:gap-[120px] gap-[30px] font-myriad-normal text-dark pt-8"
          >
            <div className="flex flex-col gap-[15px] w-full">
              <div className="flex lg:flex-row flex-col lg:gap-[120px] gap-[30px] font-myriad-normal text-dark pt-8">
                <div className="flex flex-col gap-[20px] max-w-[410.63px]">
                  <div className="flex w-full flex-col gap-[9px] text-sm leading-5 ">
                    <label htmlFor="username" className="capitalize">
                      {t("surnom")}
                    </label>
                    <TextField
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: "16px",
                          fontFamily: "Myriad Pro",
                          height: "45px",
                          padding: "0px 16px",
                        },
                        "& .MuiFormHelperText-root": {
                          textAlign: `${
                            router.locale === "ar" ? "right" : "left"
                          }`,
                        },
                      }}
                      id="username"
                      name="username"
                      value={formik.values.username}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.username &&
                        Boolean(formik.errors.username)
                      }
                      helperText={
                        formik.touched.username && t(formik.errors.username)
                      }
                      label=""
                      variant="outlined"
                      placeholder={t("Entrer votre Surnom unique")}
                      fullWidth
                    />
                  </div>

                  <div className="flex items-center gap-[20px]">
                    <div className="flex w-full flex-col gap-[9px] text-sm leading-5">
                      <label htmlFor="firstName" className="capitalize">
                        {t("Nom")}
                      </label>
                      <TextField
                        sx={{
                          "& .MuiFormHelperText-root": {
                            textAlign: `${
                              router.locale === "ar" ? "right" : "left"
                            }`,
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "16px",
                            fontFamily: "Myriad Pro",
                            height: "45px",
                            padding: "0px 16px",
                          },
                        }}
                        id="firstName"
                        name="firstName"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.firstName &&
                          Boolean(formik.errors.firstName)
                        }
                        helperText={
                          formik.touched.firstName && t(formik.errors.firstName)
                        }
                        label=""
                        variant="outlined"
                        placeholder={t("Votre Nom")}
                        fullWidth
                      />
                    </div>
                    <div className="flex w-full flex-col gap-[9px] text-sm leading-5">
                      <label htmlFor="lastName" className="capitalize">
                        {t("Prenom")}
                      </label>
                      <TextField
                        sx={{
                          "& .MuiFormHelperText-root": {
                            textAlign: `${
                              router.locale === "ar" ? "right" : "left"
                            }`,
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "16px",
                            fontFamily: "Myriad Pro",
                            height: "45px",
                            padding: "0px 16px",
                          },
                        }}
                        id="lastName"
                        name="lastName"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.lastName &&
                          Boolean(formik.errors.lastName)
                        }
                        helperText={
                          formik.touched.lastName && t(formik.errors.lastName)
                        }
                        label=""
                        variant="outlined"
                        placeholder={t("Votre Prenom")}
                        fullWidth
                      />
                    </div>
                  </div>

                  <div
                    dir="ltr"
                    className="flex w-full flex-col gap-[9px] text-sm leading-5"
                  >
                    <label
                      dir={`${router.locale === "ar" ? "rtl" : "ltr"}`}
                      htmlFor="phone"
                      className="capitalize"
                    >
                      {t("Telephone")}
                    </label>
                    <TextField
                      sx={{
                        "& .MuiFormHelperText-root": {
                          textAlign: `${
                            router.locale === "ar" ? "right" : "left"
                          }`,
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "16px",
                          fontFamily: "Myriad Pro",
                          height: "45px",
                          padding: "0px 16px",
                        },
                      }}
                      id="phone"
                      name="phone"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.phone && Boolean(formik.errors.phone)
                      }
                      helperText={
                        formik.touched.phone && t(formik.errors.phone)
                      }
                      label=""
                      variant="outlined"
                      placeholder="5 55 55 55 55"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <div
                            className={` border-[#E0E1E4] flex justify-center items-center  h-[45px] p-2
                         ${
                           router.locale === "ar"
                             ? " border-l"
                             : " pl-0 border-r"
                         }
                         `}
                          >
                            <span className="text-sm text-black font-myriad-normal">
                              +213
                            </span>
                          </div>
                        ),
                      }}
                    />
                  </div>
                  <div className="flex w-full flex-col gap-[9px] text-sm leading-5">
                    <label htmlFor="email" className="capitalize">
                      {t("adresse mail")}
                    </label>
                    <TextField
                      sx={{
                        "& .MuiFormHelperText-root": {
                          textAlign: `${
                            router.locale === "ar" ? "right" : "left"
                          }`,
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "16px",
                          fontFamily: "Myriad Pro",
                          height: "45px",
                          padding: "0px 16px",
                        },
                      }}
                      id="email"
                      name="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={
                        formik.touched.email && t(formik.errors.email)
                      }
                      label=""
                      variant="outlined"
                      placeholder={t("Entrer votre  email")}
                      fullWidth
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-[20px] max-w-[410.63px]">
                  <div className="flex w-full flex-col gap-[9px] text-sm leading-5">
                    <label htmlFor="psw" className="capitalize">
                      {t("Mot de passe")}
                    </label>
                    <TextField
                      sx={{
                        "& .MuiFormHelperText-root": {
                          textAlign: `${
                            router.locale === "ar" ? "right" : "left"
                          }`,
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "16px",
                          fontFamily: "Myriad Pro",
                          height: "45px",
                          padding: "0px 16px",
                        },
                        "& .MuiOutlinedInput-root": {
                          paddingLeft: "0",
                        },
                      }}
                      id="psw"
                      name="psw"
                      value={formik.values.psw}
                      onChange={formik.handleChange}
                      error={formik.touched.psw && Boolean(formik.errors.psw)}
                      helperText={formik.touched.psw && t(formik.errors.psw)}
                      label=""
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      placeholder={t("Entrer le mot de passe")}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <div
                            className={`absolute  px-[16px] ${
                              router.locale === "ar" ? "left-0" : "right-0"
                            }`}
                          >
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          </div>
                        ),
                      }}
                    />
                  </div>
                  <div className="flex w-full flex-col gap-[9px] text-sm leading-5">
                    <label htmlFor="address" className="capitalize">
                      {t("adresse")}
                    </label>
                    <TextField
                      sx={{
                        "& .MuiFormHelperText-root": {
                          textAlign: `${
                            router.locale === "ar" ? "right" : "left"
                          }`,
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "16px",
                          fontFamily: "Myriad Pro",
                          height: "45px",
                          padding: "0px 16px",
                        },
                      }}
                      id="address"
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.address && Boolean(formik.errors.address)
                      }
                      helperText={
                        formik.touched.address && t(formik.errors.address)
                      }
                      label=""
                      variant="outlined"
                      placeholder={t("adresse(nom de la rue ,numéro de porte)")}
                      fullWidth
                    />
                  </div>
                  <div className="flex w-full flex-col gap-[9px] text-sm leading-5">
                    <label htmlFor="province" className="capitalize">
                      {t("Province")}
                    </label>
                    <TextField
                      sx={{
                        "& .MuiFormHelperText-root": {
                          textAlign: `${
                            router.locale === "ar" ? "right" : "left"
                          }`,
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "16px",
                          fontFamily: "Myriad Pro",
                          height: "45px",
                          padding: "0px 16px",
                        },
                      }}
                      id="province"
                      name="province"
                      value={formik.values.province}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.province &&
                        Boolean(formik.errors.province)
                      }
                      helperText={
                        formik.touched.province && t(formik.errors.province)
                      }
                      label=""
                      variant="outlined"
                      placeholder={t("alger")}
                      fullWidth
                    />
                  </div>
                  <div className="flex items-center gap-[20px]">
                    <div className="flex w-full flex-col gap-[9px] text-sm leading-5">
                      <label htmlFor="wilaya" className="capitalize">
                        {t("wilaya")}
                      </label>
                      <TextField
                        sx={{
                          "& .MuiFormHelperText-root": {
                            textAlign: `${
                              router.locale === "ar" ? "right" : "left"
                            }`,
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "16px",
                            fontFamily: "Myriad Pro",
                            height: "45px",
                            padding: "0px 16px",
                          },
                        }}
                        id="wilaya"
                        name="wilaya"
                        value={formik.values.wilaya}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.wilaya && Boolean(formik.errors.wilaya)
                        }
                        helperText={
                          formik.touched.wilaya && t(formik.errors.wilaya)
                        }
                        label=""
                        variant="outlined"
                        placeholder={t("alger")}
                        fullWidth
                      />
                    </div>
                    <div className="flex w-full flex-col gap-[9px] text-sm leading-5">
                      <label htmlFor="postalCode" className="capitalize">
                        {t("Code postal")}
                      </label>
                      <TextField
                        sx={{
                          "& .MuiFormHelperText-root": {
                            textAlign: `${
                              router.locale === "ar" ? "right" : "left"
                            }`,
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "16px",
                            fontFamily: "Myriad Pro",
                            height: "45px",
                            padding: "0px 16px",
                          },
                        }}
                        id="postalCode"
                        name="postalCode"
                        value={formik.values.postalCode}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.postalCode &&
                          Boolean(formik.errors.postalCode)
                        }
                        helperText={
                          formik.touched.postalCode &&
                          t(formik.errors.postalCode)
                        }
                        label=""
                        variant="outlined"
                        placeholder="16000"
                        fullWidth
                      />
                    </div>
                  </div>
                  <button
                    disabled={loading}
                    type="submit"
                    className="bg-primary ha:hover:bg-secondary duration-300  mt-[20px] text-white font-myriad-semibold text-sm leading-5 w-full rounded font-semibold capitalize h-[45px]"
                  >
                    {loading ? <Spinner /> : t("S'inscrire")}
                  </button>
                </div>
              </div>

              <div className="h-[0.4px] w-full bg-[#9C9C9C] mt-[30px]"></div>
              <div className="flex justify-center gap-2 items-center text-dark text-sm font-myriad-normal">
                <span>{t("Vous avez un compte ?")}</span>
                <Link href="/login">
                  <span className="text-primary">{t("s'identifier")}</span>
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;
